<script setup>
import { ref, watch } from 'vue'
import api from '@/services/api'
import { useFormulario } from '@/composables/useFormulario'
import FormularioCadastro from '@/components/base/formulario/FormularioCadastro.vue'
import CampoInput from '@/components/base/campos/CampoInput.vue'
import CampoBuscaLista from '@/components/base/campos/CampoBuscaLista.vue'
import CampoSelect from '@/components/base/campos/CampoSelect.vue'

const props = defineProps({
  itemParaEditar: { type: Object, default: null },
  modeloVazio: {
    type: Object,
    default: () => ({
      idNcm: 0,
      codigoNcm: '',
      descricao: '',
      lstUnidadeEstatistica: '',
      dataInicioValidade: '',
      dataFimValidade: '',
      atributos: []
    })
  }
})

const emit = defineEmits(['itemAtualizado', 'cancelar', 'show-notification', 'changesDetected'])

const simNaoOptions = [
  { id: true, label: 'Sim' },
  { id: false, label: 'Não' }
]

const escopoOptions = [
  { id: 'PRODUTO', label: 'Cadastro do Produto (Estático)' },
  { id: 'OPERACAO', label: 'Operação de Compra/Importação (Dinâmico)' }
]

const {
  item,
  isLoading: isSaving,
  isEditing,
  entityInfo,
  submitForm,
  initializeForm,
  handleCancel
} = useFormulario(props, emit)

// --- Máscara Inteligente para NCM ---
// Remove qualquer ponto ou caractere não numérico enquanto o utilizador digita
const formatarCodigoNcm = (e) => {
  const v = e.target.value.replace(/\D/g, '')
  item.value.codigoNcm = v
}

// --- Lógica Interna dos Atributos (Memória) ---
const atributoEmEdicao = ref(null)

const iniciarNovoAtributo = () => {
  atributoEmEdicao.value = {
    _isNew: true,
    codigoSiscomex: '',
    descricao: '',
    escopo: 'PRODUTO',
    tipoDado: 'Texto',
    obrigatorio: true
  }
}

const editarAtributo = (atr, idx) => {
  atributoEmEdicao.value = { ...atr, _index: idx, _isNew: false }
}

const cancelarEdicaoAtributo = () => {
  atributoEmEdicao.value = null
}

const salvarAtributoLocal = () => {
  const atr = atributoEmEdicao.value
  if (!atr.codigoSiscomex || !atr.descricao) {
    emit('show-notification', { message: 'Código Siscomex e Descrição são obrigatórios.', type: 'warning' })
    return
  }

  const { _isNew, _index, ...dados } = atr

  if (!item.value.atributos) {
    item.value.atributos = []
  }

  if (!_isNew && typeof _index === 'number') {
    item.value.atributos[_index] = dados
  } else {
    item.value.atributos.push(dados)
  }

  atributoEmEdicao.value = null
  emit('changesDetected', true)
}

const removerAtributo = (idx) => {
  item.value.atributos.splice(idx, 1)
  emit('changesDetected', true)
}

// --- Tratamento das Datas ---
watch(() => item.value.dataInicioValidade, (newVal) => {
  if (newVal && newVal.includes('T')) item.value.dataInicioValidade = newVal.split('T')[0]
}, { immediate: true })

watch(() => item.value.dataFimValidade, (newVal) => {
  if (newVal && newVal.includes('T')) item.value.dataFimValidade = newVal.split('T')[0]
}, { immediate: true })

if (!item.value.atributos) item.value.atributos = []

// --- Submissão Completa para a API ---
const salvarNcm = async () => {
  const payload = {
    ...item.value,
    dataInicioValidade: item.value.dataInicioValidade ? new Date(`${item.value.dataInicioValidade}T00:00:00Z`).toISOString() : null,
    dataFimValidade: item.value.dataFimValidade ? new Date(`${item.value.dataFimValidade}T00:00:00Z`).toISOString() : null,
    atributos: item.value.atributos || []
  }

  await submitForm('Ncms', 'idNcm', payload)
}

const salvarAlteracoes = async () => {
  const isNew = !item.value.idNcm
  const payload = {
    ...item.value,
    dataInicioValidade: item.value.dataInicioValidade ? new Date(`${item.value.dataInicioValidade}T00:00:00Z`).toISOString() : null,
    dataFimValidade: item.value.dataFimValidade ? new Date(`${item.value.dataFimValidade}T00:00:00Z`).toISOString() : null,
    atributos: item.value.atributos || []
  }

  // Remove o ID do payload em novos cadastros para evitar conflito de PK
  if (isNew) {
    delete payload.idNcm
  }

  try {
    isSaving.value = true
    const res = isNew
      ? await api.post('/Ncms', payload)
      : await api.put(`/Ncms/${item.value.idNcm}`, payload)

    if (res?.data) {
      initializeForm(res.data)
    }
    emit('show-notification', { message: 'Alterações salvas com sucesso!', type: 'success' })
  } catch (error) {
    let msg = 'Erro ao salvar alterações.'
    if (error.response?.data?.errors) {
      msg = Object.values(error.response.data.errors).flat()[0] || msg
    } else if (error.response?.data?.message || error.response?.data?.title) {
      msg = error.response.data.message || error.response.data.title
    } else if (typeof error.response?.data === 'string') {
      msg = error.response.data
    }
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isSaving.value = false
  }
}
</script>

<template>
  <FormularioCadastro
    module-name="Manutenção de NCM"
    :is-loading="isSaving"
    :is-editing="isEditing"
    :entity-info="entityInfo"
    @submit="salvarNcm"
    @submit-continuar="salvarAlteracoes"
    @cancelar="handleCancel"
    @show-notification="(p) => emit('show-notification', p)"
  >
    <!-- Frame 1: Dados Oficiais -->
    <div class="card mb-4">
      <div class="card-header">
        <h5 class="mb-0">Dados Oficiais (Receita Federal)</h5>
      </div>
      <div class="card-body">
        <div class="row g-3">
          <div class="col-md-3">
            <CampoInput
              id="codigoNcm"
              label="Código NCM"
              v-model="item.codigoNcm"
              placeholder="Ex: 85171231"
              maxlength="8"
              @input="formatarCodigoNcm"
              required
            />
          </div>
          <div class="col-md-7">
            <CampoInput
              id="descricaoOficial"
              label="Descrição Oficial"
              v-model="item.descricao"
              required
            />
          </div>
          <div class="col-md-2">
            <CampoBuscaLista
              id="lstUnidadeEstatistica"
              label="Unid. Estatística"
              v-model="item.lstUnidadeEstatistica"
              tabela="Ncm"
              required
            />
          </div>

          <div class="col-md-3">
            <CampoInput
              id="dtInicio"
              label="Início de Validade"
              type="date"
              v-model="item.dataInicioValidade"
            />
          </div>
          <div class="col-md-3">
            <CampoInput
              id="dtFim"
              label="Fim de Validade"
              type="date"
              v-model="item.dataFimValidade"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Frame 2: Atributos do Catálogo de Produtos (CADA) -->
    <div class="card mb-3">
      <div class="card-header d-flex justify-content-between align-items-center">
        <h5 class="mb-0">Atributos Exigidos (Catálogo Portal Único)</h5>
        <button type="button" class="btn btn-primary btn-sm" @click="iniciarNovoAtributo" :disabled="!!atributoEmEdicao">
          <i class="bi bi-plus-lg me-1"></i> Adicionar Exigência
        </button>
      </div>

      <div class="card-body p-0">
        <div class="admin-table-container border-0 shadow-none mb-0">
          <table class="table admin-table m-0">
            <thead>
              <tr>
                <th style="width: 150px;">Cód. Siscomex</th>
                <th>Descrição do Atributo</th>
                <th style="width: 25%;">Escopo da Resposta</th>
                <th style="width: 120px;" class="text-center">Obrigatório</th>
                <th class="text-end" style="width: 100px;">Ações</th>
              </tr>
            </thead>
            <tbody>

              <!-- Linha de Novo Atributo / Edição -->
              <tr v-if="atributoEmEdicao" class="bg-body-tertiary">
                <td class="p-2 align-top">
                  <CampoInput id="atrCod" v-model="atributoEmEdicao.codigoSiscomex" placeholder="Ex: ATT_001" hide-label />
                </td>
                <td class="p-2 align-top">
                  <CampoInput id="atrDesc" v-model="atributoEmEdicao.descricao" placeholder="Ex: Informar a Marca..." hide-label />
                </td>
                <td class="p-2 align-top">
                  <CampoSelect id="atrEscopo" v-model="atributoEmEdicao.escopo" :options="escopoOptions" option-value="id" option-label="label" hide-label />
                </td>
                <td class="p-2 align-top text-center">
                  <CampoSelect id="atrObrig" v-model="atributoEmEdicao.obrigatorio" :options="simNaoOptions" option-value="id" option-label="label" hide-label />
                </td>
                <td class="p-2 align-top text-end acoes-cell">
                  <div class="d-flex justify-content-end gap-1">
                    <button type="button" class="btn btn-success btn-sm" @click="salvarAtributoLocal"><i class="bi bi-check-lg"></i></button>
                    <button type="button" class="btn btn-secondary btn-sm" @click="cancelarEdicaoAtributo"><i class="bi bi-x-lg"></i></button>
                  </div>
                </td>
              </tr>

              <!-- Linhas Cadastradas -->
              <tr v-for="(atr, idx) in item.atributos" :key="idx">
                <td class="fw-bold">{{ atr.codigoSiscomex }}</td>
                <td>{{ atr.descricao }}</td>
                <td>
                  <span class="badge" :class="atr.escopo === 'PRODUTO' ? 'bg-primary' : 'bg-warning text-dark'">
                    {{ atr.escopo === 'PRODUTO' ? 'No Produto' : 'Na Operação' }}
                  </span>
                </td>
                <td class="text-center">
                  <i v-if="atr.obrigatorio" class="bi bi-check-circle-fill text-danger" title="Obrigatório"></i>
                  <span v-else class="text-muted">-</span>
                </td>
                <td class="acoes-cell text-end pe-3">
                  <button type="button" class="btn-icon" @click="editarAtributo(atr, idx)" :disabled="!!atributoEmEdicao"><i class="bi bi-pencil-fill"></i></button>
                  <button type="button" class="btn-icon text-danger" @click="removerAtributo(idx)" :disabled="!!atributoEmEdicao"><i class="bi bi-trash-fill"></i></button>
                </td>
              </tr>

              <tr v-if="item.atributos && item.atributos.length === 0 && !atributoEmEdicao">
                <td colspan="5" class="text-center py-4 text-muted">Nenhum atributo aduaneiro exigido para esta NCM.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </FormularioCadastro>
</template>

<style scoped>
/* Grelhas Internas Adaptáveis herdadas do padrão */
.admin-table-container {
  background-color: transparent;
  border-top: 1px solid var(--bs-border-color-translucent);
}
.admin-table thead th {
  background-color: var(--bs-tertiary-bg);
  color: var(--bs-heading-color);
  font-weight: 600;
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid var(--bs-border-color-translucent);
}
.admin-table td {
  border-bottom: 1px solid var(--bs-border-color-translucent);
  color: var(--bs-body-color);
  vertical-align: middle;
}
.acoes-cell { text-align: right; vertical-align: middle; padding-right: 0.5rem; }
.btn-icon { background: none; border: none; color: #6c7d8e; padding: 0.4rem; transition: 0.2s; }
.btn-icon:hover { color: #212934; }
[data-bs-theme="dark"] .btn-icon:hover { color: #fff; }

:deep(.campo-input-container), :deep(.campo-select-container) {
  margin-bottom: 0 !important;
}
</style>
